/**
 * LockScreen Component
 * 
 * A fullscreen lock screen overlay that requires password entry to unlock.
 * Used when the user locks their screen or after a period of inactivity.
 * 
 * @example
 * ```typescript
 * const lockScreen = new LockScreen(container, {
 *   email: 'user@example.com',
 *   userName: 'John Doe',
 *   userAvatar: 'https://example.com/avatar.jpg',
 *   onUnlock: (user) => {
 *     console.log('Unlocked by:', user.name);
 *   },
 *   onCancel: () => console.log('Lock cancelled'),
 *   onSwitchUser: () => showLoginForm(),
 *   lockedAt: new Date()
 * });
 * await lockScreen.initialize();
 * ```
 */

import { IComponent } from '../IComponent';
import { LockScreenOptions, AuthUser } from './AuthTypes';
import { ToasterService } from '@/services/ToasterService';
import { authService } from '@/services/Authentication';
import { oemConfig } from '@/services/OEMConfig';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('LockScreen');

export class LockScreen implements IComponent {
  private container: HTMLElement;
  private options: LockScreenOptions;

  constructor(container: HTMLElement, options: LockScreenOptions) {
    this.container = container;
    this.options = {
      lockedAt: new Date(),
      ...options
    };
  }

  async initialize(): Promise<void> {
    ToasterService.init();
    this.render();
    this.bindEvents();
    logger.debug('LockScreen initialized');
  }

  destroy(): void {
    this.container.innerHTML = '';
    logger.debug('LockScreen destroyed');
  }

  /**
   * Render the lock screen HTML
   */
  private render(): void {
    const oem = {
      logo: oemConfig.getSplashImage(),
      caption: oemConfig.getBrandName()
    };

    const lockedAtTime = this.options.lockedAt?.toLocaleTimeString() || new Date().toLocaleTimeString();
    const avatarUrl = this.options.userAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(this.options.userName)}`;

    this.container.innerHTML = `
      <div class="lock-screen-container flex items-center justify-center min-h-screen bg-base-200">
        <div class="w-full max-w-md p-8">
          <div class="text-center mb-8">
            <img src="${oem.logo}" alt="${oem.caption}" class="h-20 mx-auto mb-6">
            <div class="avatar mb-4">
              <div class="w-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
                <img src="${avatarUrl}" alt="User Avatar" />
              </div>
            </div>
            <h2 class="text-2xl font-bold">${this.options.userName}</h2>
            <p class="text-base-content/70 mt-2">Screen is locked</p>
          </div>

          <form id="lock-screen-form-component" class="space-y-4">
            <!-- Password -->
            <div class="form-control">
              <label class="label">
                <span class="label-text">Password</span>
              </label>
              <input 
                type="password" 
                id="lock-screen-password-input"
                name="password" 
                autocomplete="current-password"
                placeholder="Enter your password to unlock" 
                class="input input-bordered w-full input-lg" 
                required 
                autofocus
              />
            </div>

            <!-- Buttons -->
            <div class="flex gap-2">
              <button type="submit" id="lock-screen-submit-btn" class="btn btn-primary flex-1">
                <i class="fas fa-unlock"></i>
                Unlock
              </button>
              ${this.options.onCancel ? `
                <button type="button" id="lock-screen-cancel-btn" class="btn btn-ghost">
                  <i class="fas fa-times"></i>
                  Cancel
                </button>
              ` : ''}
            </div>
          </form>

          <div class="text-center text-sm mt-6 space-y-2">
            ${this.options.onSwitchUser ? `
              <p>
                <a role="button" id="lock-screen-switch-user-link" class="link link-primary cursor-pointer">Switch user</a>
              </p>
            ` : ''}
            <p class="text-base-content/50 text-xs mt-4">
              Locked at ${lockedAtTime}
            </p>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    const form = this.container.querySelector('#lock-screen-form-component') as HTMLFormElement;
    const cancelBtn = this.container.querySelector('#lock-screen-cancel-btn');
    const switchUserLink = this.container.querySelector('#lock-screen-switch-user-link');

    // Form submission
    form?.addEventListener('submit', (e) => this.handleSubmit(e));

    // Cancel button
    cancelBtn?.addEventListener('click', () => {
      this.options.onCancel?.();
    });

    // Switch user link
    switchUserLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.options.onSwitchUser?.();
    });
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const password = formData.get('password') as string;

    if (!password) {
      ToasterService.warning('Please enter your password');
      return;
    }

    const submitBtn = this.container.querySelector('#lock-screen-submit-btn') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Unlocking...';

    try {
      const result = await authService.login({ email: this.options.email, password });

      if (result.success) {
        const user: AuthUser = {
          id: result.user?.id || 'user-1',
          email: this.options.email,
          name: this.options.userName,
          avatar: this.options.userAvatar
        };

        ToasterService.success('Screen unlocked successfully');
        this.options.onUnlock(user);
      } else {
        ToasterService.error(result.message || 'Failed to unlock. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
      }
    } catch (error) {
      logger.error('Failed to unlock:', error);
      ToasterService.error('Failed to unlock. Please try again.');
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  }
}
